import {
  FaQrcode,
  FaMobileAlt,
  FaChartLine,
  FaReceipt,
  FaCogs,
  FaUserShield,
} from "react-icons/fa";
import { motion } from "framer-motion";
import CircularGallery from "./CircularGallery";
import { useNavigate } from "react-router-dom";

const features = [
  {
    icon: <FaQrcode className="text-3xl sm:text-4xl" />,
    title: "QR Code Menus",
    desc: "Unique QR codes for every table enable guests to instantly access digital menus and order contactlessly.",
    gradient: "from-orange-500 to-red-500",
    tech: ["Contactless", "Table-specific", "Instant Access"],
  },
  {
    icon: <FaMobileAlt className="text-3xl sm:text-4xl" />,
    title: "Contactless Ordering",
    desc: "Customers scan, browse, and place orders from their device. Orders are sent directly to the kitchen in real time.",
    gradient: "from-red-500 to-pink-500",
    tech: ["No App Needed", "Real-time", "Mobile Friendly"],
  },
  {
    icon: <FaChartLine className="text-3xl sm:text-4xl" />,
    title: "Analytics Dashboard",
    desc: "Restaurant owners get real-time sales, order, and item analytics to optimize operations and menu.",
    gradient: "from-yellow-500 to-orange-500",
    tech: ["Sales Trends", "Top Items", "Live Stats"],
  },
  {
    icon: <FaReceipt className="text-3xl sm:text-4xl" />,
    title: "Commission Billing",
    desc: "Automated commission calculation and transparent billing for every order processed through the platform.",
    gradient: "from-green-500 to-lime-500",
    tech: ["Auto Billing", "Transparent Fees", "Order-linked"],
  },
  {
    icon: <FaCogs className="text-3xl sm:text-4xl" />,
    title: "Admin Portal",
    desc: "Powerful web portal for restaurant managers to update menus, manage tables, track orders, and view analytics.",
    gradient: "from-teal-500 to-cyan-500",
    tech: ["Robotic Chefs", "IoT Automation", "AI Optimization"],
  },
  {
    icon: <FaUserShield className="text-3xl sm:text-4xl" />,
    title: "Mobile Menu & Ordering",
    desc: "Customers scan the table QR code to instantly access your digital menu and place orders directly from their mobile device - no app download required.",
    gradient: "from-blue-500 to-indigo-500",
    tech: ["QR Scanning", "No App Needed", "Instant Access"],
  },
  {
    icon: <FaCogs className="text-3xl sm:text-4xl" />,
    title: "Customised Websites",
    desc: "We deliver bespoke, branded websites for hotels and restaurants, tailored to your unique identity and business needs.",
    gradient: "from-blue-500 to-cyan-500",
    tech: ["Branding", "Custom Design", "SEO Friendly"],
  },
];

const partnersTrusted = [
  {
    name: "Hiwa Boutique Hotel Pvt. Ltd",
    desc: "A premium hospitality client leveraging our QR-based digital menu and ordering platform for a seamless guest experience.",
    icon: <FaUserShield className="text-2xl sm:text-3xl" />,
  },
  {
    name: "Signature Groups Of Hotels",
    desc: "Trusted by Signature Groups for robust admin portals, real-time analytics, and custom digital solutions across their properties.",
    icon: <FaReceipt className="text-2xl sm:text-3xl" />,
  },
  {
    name: "Oak by Signature Group",
    desc: "Oak Hotel integrates our digital menu and mobile scanning to enhance guest ordering and service in their rooms.",
    icon: <FaChartLine className="text-2xl sm:text-3xl" />,
  },
];

function Features() {
  const navigate = useNavigate();
  return (
    <section id="features" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10">
        {/* Feature Container */}
        <div className="relative h-auto overflow-visible rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl bg-gradient-to-br from-black via-gray-900 to-gray-800 p-3 sm:p-4 md:p-6 lg:p-8 xl:p-12">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-gradient-to-r from-orange-500/10 to-red-500/10"
                initial={{
                  x: Math.random() * 100,
                  y: Math.random() * 100,
                  width: Math.random() * 300 + 100,
                  height: Math.random() * 300 + 100,
                  opacity: 0.1,
                }}
                animate={{
                  x: [null, Math.random() * 100 - 50],
                  y: [null, Math.random() * 100 - 50],
                  opacity: [0.1, 0.15, 0.1],
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />
            ))}
          </div>

          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <motion.h2
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-white mb-3 sm:mb-4 md:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-500">
                Future-Ready Restaurant Tech
              </span>
            </motion.h2>
            <motion.p
              className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Experience the next generation of dining technology with our
              cutting-edge platform
            </motion.p>
          </div>

          {/* Main Features Carousel (Circular Animation) */}
          <div className="relative h-[250px] sm:h-[300px] md:h-[400px] lg:h-[500px] xl:h-[550px] mb-12 sm:mb-16 md:mb-24 lg:mb-32 flex items-center justify-center overflow-hidden">
            <CircularGallery
              items={features}
              bend={3}
              textColor="#fff"
              borderRadius={0.08}
              scrollSpeed={2}
              scrollEase={0.08}
              renderCard={(feature, index) => (
                <div
                  key={index}
                  className="group relative bg-gray-800/80 backdrop-blur-lg rounded-lg sm:rounded-xl md:rounded-2xl lg:rounded-3xl p-2 sm:p-3 md:p-4 lg:p-6 xl:p-8 shadow-2xl border border-gray-700 overflow-hidden h-full w-full flex flex-col items-center justify-center"
                >
                  {/* Icon Container */}
                  <div className="relative z-10 mb-2 sm:mb-3 md:mb-4 lg:mb-6 xl:mb-8">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 xl:w-18 xl:h-18 bg-gradient-to-br ${feature.gradient} rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      {feature.icon}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="relative z-10 text-center flex-1 flex flex-col justify-center">
                    <h3 className="font-bold mb-1 sm:mb-1.5 md:mb-2 lg:mb-3 xl:mb-4 text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-white group-hover:text-orange-400 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="leading-relaxed mb-2 sm:mb-3 md:mb-4 lg:mb-5 xl:mb-6 text-gray-300 text-xs sm:text-sm md:text-base flex-1">
                      {feature.desc}
                    </p>
                    {/* Tech badges */}
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 md:gap-2 justify-center">
                      {feature.tech.map((tech, i) => (
                        <span
                          key={i}
                          className="text-xs font-medium px-1 sm:px-1.5 md:px-2 lg:px-3 py-0.5 sm:py-1 rounded-full bg-gray-700/50 text-orange-300 border border-gray-600"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Hover Effect */}
                  <div
                    className={`absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl lg:rounded-3xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}
                  ></div>
                </div>
              )}
            />
          </div>

          {/* Partners & Trusted By */}
          <div className="mb-12 sm:mb-16 md:mb-24 lg:mb-32">
            <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center">
              Partners & Trusted By
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8">
              {partnersTrusted.map((item, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center bg-white/10 rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg"
                >
                  <div className="mb-2 sm:mb-3 md:mb-4 text-primary-400">
                    {item.icon}
                  </div>
                  <h4 className="text-sm sm:text-base md:text-lg font-semibold text-white mb-1.5 sm:mb-2 text-center">
                    {item.name}
                  </h4>
                  <p className="text-gray-200 text-center text-xs sm:text-sm">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-12 sm:mb-16 md:mb-24 lg:mb-32">
            {[
              {
                number: "300%",
                label: "Faster Order Processing",
                icon: "⚡",
                desc: "With Scanning QR Code",
              },
              {
                number: "99.9%",
                label: "Uptime Guarantee",
                icon: "🛡️",
                desc: "Blockchain-backed reliability",
              },
              {
                number: "2.5x",
                label: "More Customer Engagement",
                icon: "👥",
                desc: "Through neuro-responsive menus",
              },
            ].map((stat, index) => (
              <motion.div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 border border-gray-700 hover:border-orange-400/50 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-1.5 sm:mb-2 md:mb-3 lg:mb-4">
                  {stat.icon}
                </div>
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-red-500 mb-1 sm:mb-2">
                  {stat.number}
                </div>
                <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-white mb-1 sm:mb-2">
                  {stat.label}
                </div>
                <div className="text-xs sm:text-sm md:text-base text-gray-400">
                  {stat.desc}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Call to Action */}
          <motion.div
            className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl sm:rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl border border-gray-700 max-w-5xl mx-auto relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden opacity-20">
              <div className="absolute top-1/4 left-1/4 w-24 sm:w-32 md:w-48 lg:w-64 h-24 sm:h-32 md:h-48 lg:h-64 bg-orange-500 rounded-full filter blur-3xl animate-[pulse_5s_ease-in-out_infinite]"></div>
              <div className="absolute bottom-1/3 right-1/3 w-20 sm:w-24 md:w-36 lg:w-48 h-20 sm:h-24 md:h-36 lg:h-48 bg-red-500 rounded-full filter blur-3xl animate-[pulse_7s_ease-in-out_infinite]"></div>
            </div>

            <div className="relative z-10 text-center">
              <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-3 sm:mb-4 md:mb-6">
                Ready for the Future of Dining?
              </h3>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-4 sm:mb-6 md:mb-8 max-w-3xl mx-auto px-2">
                Join the revolution with EasiDine's next-generation restaurant
                technology platform
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 justify-center">
                <button
                  className="px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-lg sm:rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-orange-500/30 text-sm sm:text-base"
                  onClick={() => navigate("/login")}
                >
                  Start Free Trial
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default Features;
